import React, { Component } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import update from 'immutability-helper';
import Filter from './Filter';
import TreeSelector from '../TreeSelector';
import Collapse from '../Collapse';

export default class TagFilter extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    ontology: PropTypes.object.isRequired,
    onChange: PropTypes.func,
    query: PropTypes.array,
  }

  static defaultProps = {
    onChange() {},
    query: [],
  }

  constructor(props) {
    super(props);

    const ontologyInitialState = Object.keys(this.props.ontology).reduce((ontologyAcc, ontologyType) => {
      ontologyAcc[ontologyType] = { ...this.props.ontology[ontologyType], id: ontologyType, expanded: false };
      return ontologyAcc;
    }, {});

    this.state = {
      query: [],
      ontology: ontologyInitialState,
      filteredOntology: ontologyInitialState,
      activeFilteredOntology: false,
    };
  }

  // Returns true if the "query" string is included in the "text" string.
  // It's case insensitive and splits the phrases in words. For eg:
  // `search("account leg", "Accounting & Legal")` returns true.
  search(query, text) {
    const lowerQ = query.toLowerCase();
    const lowerT = text.toLowerCase();
    return lowerQ.split(' ').map(term => lowerT.indexOf(term) >= 0).reduce((acc, val) => (acc && val), true);
  }

  handleFilterInputChange = (e) => {
    const q = e.target.value;

    if (q.length === 0) {
      this.setState({
        ontology: this.state.ontology,
        filteredOntology: {},
        activeFilteredOntology: false,
        query: [],
      });
    } else {
      this.setState({
        filteredOntology: Object.values(this.state.ontology).reduce((ontologyAcc, ontologyType) => {
          const options = Object.values(ontologyType.options).reduce((acc, item) => {
            if ((item.children === undefined || item.children.length === 0)
              && item.name.split(' ').filter((s) => s.toUpperCase().startsWith(q.toUpperCase())).length > 0) {
              acc.push(item);
            } else if (item.children !== undefined && item.children.length > 0) {
              const childrenOptions = Object.values(item.children).reduce((childAcc, childItem) => {
                if (this.search(q, childItem.name)) {
                  childAcc.push({ ...childItem });
                }
                return childAcc;
              }, []);
              if (Object.keys(childrenOptions).length > 0
                || this.search(q, item.name)) {
                acc.push({ ...item, children: childrenOptions, expanded: true });
              }
            }
            return acc;
          }, []);
          if (Object.keys(options).length > 0) {
            ontologyAcc[ontologyType.id] = { ...ontologyType, options, expanded: true };
          }
          return ontologyAcc;
        }, {}),
        activeFilteredOntology: true,
        query: [q],
      });
    }
  }

  resetState() {
    const params = qs.parse(location.search.slice(1));
    const ontology = Object.keys(this.state.ontology);
    const stateUpdates = {};
    const filteredStateUpdates = {};
    const query = { $set: [] };
    const activeFilteredOntology = { $set: false };

    // Update url and state (without mutating)
    ontology.forEach((ontologyType) => {
      if (params[this.props.id] !== undefined) {
        delete params[this.props.id][ontologyType];
      }
      stateUpdates[ontologyType] = { selected: { $set: [] }, expanded: { $set: false } };
      if (this.state.filteredOntology[ontologyType] !== undefined) {
        filteredStateUpdates[ontologyType] = { selected: { $set: [] }, expanded: { $set: false } };
      }
    });

    return {
      stateUpdates, filteredStateUpdates, params, query, activeFilteredOntology,
    };
  }

  handleLocalResetClick = () => {
    const {
      stateUpdates, filteredStateUpdates, params, query, activeFilteredOntology,
    } = this.resetState();

    // Persist new url and state
    this.setState(update(this.state, {
      ontology: stateUpdates, filteredOntology: filteredStateUpdates, query, activeFilteredOntology,
    }), () => {
      router.push({
        pathname: location.pathname,
        search: qs.stringify(params, { arrayFormat: 'brackets', depth: 2 }),
        state: { revisitWithSearch: true },
      });
      this.props.onChange('ontology', {
        id: this.props.id, ontology: this.state.ontology, query, activeFilteredOntology,
      });
    });
  }

  handleGlobalResetClick = () => {
    const {
      stateUpdates, filteredStateUpdates, query, activeFilteredOntology,
    } = this.resetState();

    // Persist new state
    this.setState(update(this.state, {
      ontology: stateUpdates, filteredOntology: filteredStateUpdates, query, activeFilteredOntology,
    }), () => {
      this.props.onChange('ontology', {
        id: this.props.id, ontology: this.state.ontology, query, activeFilteredOntology,
      });
    });
  }

  handleOntologyExpandedChange(ontology) {
    this.setState(update(this.state, { ontology: { [ontology.id]: { expanded: { $set: ontology.expanded } } } }));
  }

  isSelected(ontologyType, optionSlug) {
    const { selected } = this.state.ontology[ontologyType];
    return selected.filter((selectedOption) => selectedOption.id === optionSlug).length > 0;
  }

  /**
   * Check if option is already selected
   * If so, remove it from the selected state attribute and update the URL
   * Else add it to the selected state attribute and update the URL
   */
  toggleOptions(ontologyType, options) {
    const params = qs.parse(window.location.search.slice(1));
    let { selected } = this.state.ontology[ontologyType];
    const { filteredOntology } = this.state;

    params[this.props.id] = params[this.props.id] || {};
    params[this.props.id][ontologyType] = params[this.props.id][ontologyType] || [];

    // If there is a page param, reset it to page 1, since a filter are changing
    if (params.page) delete params.page;

    options.forEach((option) => {
      if (this.isSelected(ontologyType, option.id)) {
        params[this.props.id][ontologyType].splice(params[this.props.id][ontologyType].indexOf(option.id), 1);
        selected = selected.filter((selectedOption) => selectedOption.id !== option.id);
      } else {
        params[this.props.id][ontologyType].push(option.id);
        selected.push(option);
      }
    });

    let newState = update(this.state, {
      ontology: {
        [ontologyType]: {
          selected: { $set: selected },
        },
      },
    });

    if (filteredOntology[ontologyType] !== undefined) {
      newState = update(newState, {
        filteredOntology: {
          [ontologyType]: {
            selected: { $set: selected },
          },
        },
      });
    }

    // Persist new url and state
    this.setState(newState, () => {
      router.push({
        pathname: window.location.pathname,
        search: qs.stringify(params, { arrayFormat: 'brackets', depth: 2 }),
        state: { revisitWithSearch: true },
      });
      this.props.onChange('ontology', { id: this.props.id, ontology: this.state.ontology });
    });
  }

  handleOptionChange = (ontologyType, selectedOption) => {
    const { selected } = this.state.ontology[ontologyType];
    const option = (
      this.state.ontology[ontologyType].options.find((o) => o.slug === selectedOption.id)
        || this.state.ontology[ontologyType].options.reduce((acc, v) => acc.concat(v.children), []).find((o) => o.slug === selectedOption.id)
    );
    const optionsToUpdate = [selectedOption];
    const optionChildren = option.children ? Object.values(option.children) : [];

    if (option.parent_id !== null) {
      // Update parent
      // - Deselect it if it was already selected
      // - Select it if every other child option is already selected
      const parent = this.state.ontology[ontologyType].options.find((o) => o.id === option.parent_id);
      if (
        this.isSelected(ontologyType, parent.slug)
          || (!this.isSelected(ontologyType, option.slug)
          && Object.keys(parent.children).length - 1 === selected.filter((o) => (
            Object.values(parent.children).filter((childOption) => childOption.slug === o.id).length > 0
          )).length)) {
        optionsToUpdate.push({ id: parent.slug, name: parent.name, parent_id: null });
      }
    } else if (optionChildren.length > 0) {
      // update childs
      if (this.isSelected(ontologyType, option.slug)) {
        // this option was already selected
        optionChildren
          .forEach((childOption) => optionsToUpdate.push({ ...childOption, id: childOption.slug }));
      } else {
        // this option was not selected
        optionChildren.filter(
          (childOption) => selected.filter(
            (o) => o.id === childOption.slug
          ).length === 0
        ).forEach((childOption) => optionsToUpdate.push({ ...childOption, id: childOption.slug }));
      }
    }

    this.toggleOptions(ontologyType, optionsToUpdate);
  }

  renderOntologyType(ontologyType) {
    const options = Object.values(ontologyType.options).reduce((acc, item) => {
      if (item.children !== undefined) {
        acc[item.id] = { ...item, id: item.slug };
        acc[item.id].children = Object.values(item.children).reduce((childAcc, childItem) => {
          childAcc[childItem.id] = { ...childItem, id: childItem.slug };
          childAcc[childItem.id].children = {};
          return childAcc;
        }, {});
      } else {
        acc[item.id] = { ...item, slug: item.id, children: {} };
      }
      return acc;
    }, {});

    return (
      <li id={`ontologyType_${ontologyType.id}`} key={`ontologyType_${ontologyType.id}`}>
        <Collapse
          id={ontologyType.id}
          label={ontologyType.title}
          key={`collapse_${ontologyType.id}`}
          triggerType="tree"
          secondaryLabel={ontologyType.selected.length === 0 ? null : `(${ontologyType.selected.length} tags)`}
          expanded={ontologyType.expanded}
          expandedChange={this.handleOntologyExpandedChange.bind(this)}
        >
          <TreeSelector
            id={ontologyType.id}
            options={options}
            onChange={this.handleOptionChange.bind(this)}
            selected={Object.values(ontologyType.selected).reduce((acc, item) => {
              acc.push(item.id);
              return acc;
            }, [])}
            query={this.state.query}
          />
        </Collapse>
      </li>
    );
  }

  render() {
    const {
      id, title, expanded, onChange, type,
    } = this.props;

    const selected = Object.values(this.state.ontology).reduce((acc, category) => {
      acc.push(category.selected);
      return acc;
    }, []).flat();

    return (
      <Filter id={id} title={title} type={type} expanded={expanded} hasSelected={selected.length > 0} onChange={onChange} onLocalReset={this.handleLocalResetClick} onGlobalReset={this.handleGlobalResetClick}>
        <div className="Form u-px-4">
          <input type="text" placeholder="Type to filter suggestions" className="u-rounded u-mb-0" value={this.state.query} onChange={this.handleFilterInputChange} />
        </div>
        <ul className="Form u-m-2">
          {Object.values(this.state.activeFilteredOntology ? this.state.filteredOntology : this.state.ontology)
            .map((ontologyType) => this.renderOntologyType(ontologyType))}
        </ul>
      </Filter>
    );
  }
}
